import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/pro-light-svg-icons/faLink';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { useLinkCatalogItemMutation, useMarkCatalogItemReleasedMutation } from '../../../../../redux/api/fymAPI';
import { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { looksLikeUrl, toResolvableUrl } from '../../../creationSheet/looksLikeUrl';
import { useDebouncedValue } from '../../useDebouncedValue';
import { useCatalogMaidForRelease } from '../../useCatalogMaidForRelease';
import { useLinkSong } from '../../../../onboarding/addSong/useLinkSong';
import { SongResultRow } from '../../../../onboarding/addSong/SongResultRow';
import { addSongMessages } from '../../../../onboarding/addSong/addSongMessages';
import { addSongFlowMessages } from '../addSongFlowMessages';
import { addSongErrorOf, AddSongErrorKind } from '../addSongError';
import { TrackSave } from '../useSavingCount';
import { StepHeader } from '../StepHeader';
import { StepActions } from '../StepActions';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from '../CatalogAddSong.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface LinkStepProps {
  accountId: number;
  item: CatalogItem;
  artistName: string;
  eyebrow: string;
  track: TrackSave;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
  /** "Not out yet? Add the codes instead". */
  onCodesInstead: () => void;
}

const LINK_DELAY_MS = 300;

/**
 * Step 3 of a typed draft that is already out (design catalog-add-song c4). A song that the music
 * catalog holds is linked in place (`POST .../catalog/:uuid/link { maid }`). A pasted link makes the
 * draft a released song with the links, the cover and the ISRC of the link
 * (`POST .../catalog/:uuid/released`).
 */
const LinkStepComponent: React.FC<LinkStepProps> = ({
  accountId,
  item,
  artistName,
  eyebrow,
  track,
  onBack,
  onNext,
  onSkip,
  onCodesInstead,
}) => {
  const intl = useIntl();
  const [value, setValue] = useState('');
  const [error, setError] = useState<AddSongErrorKind>();
  const debounced = useDebouncedValue(value, LINK_DELAY_MS);
  const url = looksLikeUrl(debounced) ? toResolvableUrl(debounced) : undefined;
  const link = useLinkSong(url, artistName);
  const matchMaid = useCatalogMaidForRelease(item.title, item.artistName);
  const [linkItem, linking] = useLinkCatalogItemMutation();
  const [markReleased, marking] = useMarkCatalogItemReleasedMutation();
  const isSaving = linking.isLoading || marking.isLoading;

  const linkMatch = useCallback(() => {
    if (matchMaid === undefined) {
      return;
    }
    setError(undefined);
    track(linkItem({ accountId, itemUuid: item.uuid, maid: matchMaid }).unwrap())
      .then(() => onNext())
      .catch((failure: unknown) => setError(addSongErrorOf(failure)));
  }, [accountId, item.uuid, linkItem, matchMaid, onNext, track]);

  const savePastedLink = useCallback(() => {
    const serviceLinks = link.input?.serviceLinks;
    if (serviceLinks === undefined || Object.keys(serviceLinks).length === 0) {
      return;
    }
    setError(undefined);
    track(
      markReleased({
        accountId,
        itemUuid: item.uuid,
        serviceLinks,
        artworkUrl: link.input?.artworkUrl,
        isrc: link.input?.isrc,
      }).unwrap()
    )
      .then(() => onNext())
      .catch((failure: unknown) => setError(addSongErrorOf(failure)));
  }, [accountId, item.uuid, link.input, markReleased, onNext, track]);

  const hasPastedLinks = link.input?.serviceLinks !== undefined && Object.keys(link.input.serviceLinks).length > 0;

  return (
    <>
      <StepHeader
        eyebrow={eyebrow}
        question={intl.formatMessage(addSongFlowMessages.linkStepQuestion, { title: item.title })}
        sub={intl.formatMessage(addSongFlowMessages.linkStepSub)}
      />
      {matchMaid !== undefined && (
        <>
          <div className={styles.match} data-testid="add-song-link-match">
            <span className={styles.grow}>
              {intl.formatMessage(addSongFlowMessages.linkStepMatch, { title: item.title, artist: item.artistName })}
            </span>
            <SMLButton color="primary" omitCap onClick={linkMatch} isLoading={linking.isLoading} disabled={isSaving}>
              {intl.formatMessage(addSongFlowMessages.linkStepYes)}
            </SMLButton>
          </div>
          <div className={styles.or}>{intl.formatMessage(addSongFlowMessages.linkStepOr)}</div>
        </>
      )}
      <div className={styles.field}>
        <label className={styles.label} htmlFor="add-song-link">
          {intl.formatMessage(addSongFlowMessages.linkStepLabel)}
        </label>
        <input
          id="add-song-link"
          className={clsx(styles.input, styles.inputCode, link.state === 'bad' && styles.inputBad)}
          value={value}
          placeholder="https://open.spotify.com/track/…"
          disabled={isSaving}
          onChange={(event) => setValue(event.target.value)}
        />
        {link.state === 'resolving' && (
          <span className={styles.hint}>{intl.formatMessage(addSongMessages.resolving)}</span>
        )}
        {link.state === 'bad' && (
          <span className={styles.error} role="alert">
            {intl.formatMessage(addSongMessages.errorBadLink)}
          </span>
        )}
      </div>
      {link.input !== undefined && (
        <div className={styles.match}>
          <span className={styles.kbox}>
            <FontAwesomeIcon icon={faLink} />
          </span>
          <div className={styles.grow}>
            <SongResultRow
              id={link.input.title}
              title={link.input.title}
              subtitle={
                link.sourceLabel === undefined
                  ? link.input.artistName
                  : `${link.input.artistName} · ${link.sourceLabel}`
              }
              imageUrl={link.imageUrl}
              isPicked
              disabled={isSaving}
            />
          </div>
          <SMLButton
            color="primary"
            omitCap
            onClick={savePastedLink}
            isLoading={marking.isLoading}
            disabled={isSaving || !hasPastedLinks}
            testID="add-song-use-link"
          >
            {intl.formatMessage(addSongFlowMessages.linkStepUse)}
          </SMLButton>
        </div>
      )}
      <div>
        <button type="button" className={styles.linkButton} onClick={onCodesInstead}>
          {intl.formatMessage(addSongFlowMessages.linkStepNotOut)}
        </button>
      </div>
      {error !== undefined && (
        <div className={styles.alertError} role="alert">
          {error === 'exists'
            ? intl.formatMessage(addSongMessages.errorExists, { title: link.input?.title ?? item.title })
            : intl.formatMessage(
                error === 'readOnly' ? addSongFlowMessages.readOnlyArtist : addSongFlowMessages.saveFailed
              )}
        </div>
      )}
      <StepActions onBack={onBack} onSkip={onSkip} disabled={isSaving} />
    </>
  );
};

export const LinkStep = TypedMemo(LinkStepComponent);
